/// <reference types="@types/googlemaps" />
import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';

import { WorkflowService } from '../workflow/workflow.service';
import { AppInsightsService, GoogleTagManagerService } from '@cudirect/shared-ui-library';
import { GoogleMapsService } from '../async/google-maps.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldInstance } from '@workflow/class/field.service';
import { AnalyticsMetadataService } from '@app/service/analytics-metadata.service';

/**
 * `AddressService` facilitates address lookup functionality using Google Maps Places API.
 * It provides autocomplete functionality for address input fields and ensures the parsed address details
 * are mapped to appropriate application fields.
 *
 * TODO: We have 2 identical files here, this one is for Workflow Application - User Primary Residence Address
 * projects\cudirect-widgets\dynamic-workflow\src\lib\service\address\address.service.ts
 * This one is used on Post App Conusmer Funding. Post app should not be referencing any Workflow services as a go forward practice
 * projects\cudirect-widgets\dynamic-workflow\ecp-workflow\dynamic-app\service\address\address.service.ts
 */
@Injectable({
  providedIn: 'root'
})
export class AddressService {
  constructor(
    private readonly workflowService: WorkflowService,
    protected readonly applicationInsightsService: AppInsightsService,
    private readonly gooleMapsService: GoogleMapsService,
    private readonly statefulService: StatefulService,
    private readonly gtm: GoogleTagManagerService,
    private readonly analyticsMetadataService: AnalyticsMetadataService
  ) {}

  /**
   * Enables Google Places Autocomplete functionality on a provided input element and maps
   * the selected address details to either application fields or a post-application object.
   *
   * @param field - Contains information and configuration about the input field.
   * @param inputElem - HTML input element to enable autocomplete on.
   * @param isPostApp - Flag to determine if the function is being called post-application.
   * @param postAppAutoAddressConfig - Configuration mapping for post-application.
   * @param addressObj - Object to store address components during post-application phase.
   */
  public autocomplete(
    field: FieldInstance,
    inputElem: HTMLInputElement,
    isPostApp: boolean = false,
    postAppAutoAddressConfig?: string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    addressObj?: any
  ): Subscription {
    // Activate autocomplete for fields with `autoAddress` config or during post-application phase.
    if (field?.config?.autoAddress || isPostApp) {
      return this.gooleMapsService.getGoogleMaps().subscribe(() => {
        const autocomplete = new google.maps.places.Autocomplete(inputElem, {
          fields: ['name', 'address_components'],
          componentRestrictions: { country: 'US' },
          types: ['address']
        });

        // Event listener for when a user selects an address from dropdown suggestions.
        google.maps.event.addListener(autocomplete, 'place_changed', () => {
          this.logEvent('ecp.ui.usedGoogleAddressPrefill');
          const data = this.analyticsMetadataService.getMetadata(this.workflowService.currentPage);
          this.gtm.publishEvent(data[0] + '-address-autocomplete', data[1]);
          // Fetch detailed information about the selected location.
          const place = autocomplete.getPlace();

          // Extract specific address parts (city, state, ZIP) and map them.
          ['locality', 'administrative_area_level_1', 'postal_code'].forEach((item, i) => {
            if (!isPostApp) {
              const fieldRef = field.config.autoAddress[i];
              if (fieldRef && typeof fieldRef === 'string') {
                const iterationId = String(field.iterationId ? field.iterationId : 0);
                const fieldId = field.config.autoAddress[i].replace('$', iterationId);
                this.statefulService.wrap(fieldId, null, field.pageIterationId).value = this.getAddrPart(place, item);
              }
            } else {
              addressObj[postAppAutoAddressConfig[i]] = this.getAddrPart(place, item);
            }
          });

          // Construct street address by combining street number and street name.
          let streetAdress = `${this.getAddrPart(place, 'street_number')} ${this.getAddrPart(place, 'route')}`;
          streetAdress = streetAdress?.trim();
          if (field.fieldMaxLength > 0 && streetAdress?.length > field.fieldMaxLength) {
            streetAdress = streetAdress.slice(0, field.fieldMaxLength);
          }

          if (streetAdress?.trim()) {
            if (!isPostApp) {
              field.data.value = streetAdress;
              // refresh primary input element with streetAddress
              inputElem.value = streetAdress;
            } else {
              addressObj.addressLine1 = streetAdress;
              inputElem.focus();
              inputElem.blur();
            }
          } else {
            // clearing existing value if no street address found
            inputElem.value = '';
            inputElem.focus();
          }
        });
      });
    }
  }

  public unsubscribe(subscription: Subscription) {
    subscription.unsubscribe();
  }

  /**
   * Extracts a specific component of an address.
   *
   * @param place - Complete address details of a location.
   * @param name - Name of the address component to extract.
   * @returns Extracted address component or an empty string.
   */
  private getAddrPart(place: google.maps.places.PlaceResult, name: string): string {
    const component = place.address_components.find((c) => c.types[0] === name);
    return component?.[name === 'administrative_area_level_1' ? 'short_name' : 'long_name'] || '';
  }

  /**
   * Logs a custom event to Application Insights.
   *
   * @param eventName - Name of the event to log.
   */
  private logEvent(eventName: string): void {
    this.applicationInsightsService.logEvent(eventName, {
      eventState: 'application-flow',
      status: 'success'
    });
  }
}
